/**
 * PageStructure — renders `DistributionStyle.PAGE`.
 *
 * Serves 55 of the 85 non-hub anchors. The census that sized it found those 55
 * are two clusters differing only in whether the gutter sits on a wrapper, so
 * `maxWidth` absorbs both rather than forking the component.
 *
 * ## Same contract as HubStructure
 *
 * It positions BOXES and never looks inside one. Slots arrive with a role and a
 * footprint; `PAGE_FORM` maps each hub footprint to its page equivalent, so a
 * single slot config renders in either structure and cannot drift into two
 * configurations. That mapping is not invented here — it is the <=1180px rule
 * already present in all seven hub anchors.
 *
 * ## Why a grid track here and a float in HUB
 *
 * A page rail is ALWAYS PRESENT, so a track is right. A hub column is
 * CONTESTED — it comes and goes — so a track would reserve its width for the
 * whole page height and leave a gutter running to the footer. Same-looking
 * problem, opposite correct answer. This is the reason the two structures exist
 * rather than one with a flag.
 */

import React from "react";
import {SurfaceSlot, SlotFootprint, SlotRole, PAGE_FORM, activeSlots} from "../slots";
import "./PageStructure.scss";

/** Where the body's rail sits, or that there isn't one. */
export type PageBodyVariant = "single" | "rail-right" | "rail-left";

export interface PageStructureProps {
    slots: readonly SurfaceSlot[];
    renderSlot: (slot: SurfaceSlot) => React.ReactNode;
    /**
     * `"full"` gives the stacked cluster (32 anchors, no wrapper); a number
     * gives the centered cluster (23). One prop, both clusters.
     */
    maxWidth?: number | "full";
    /** 32 (23 anchors) or 48 (21). The two the corpus actually settled on. */
    gutter?: 32 | 48;
    /**
     * Header height. Exposed because every sticky rail offset in the corpus is
     * hand-computed from it, which is why the 54px and 56px families disagree
     * by 2px in every rail.
     */
    headerHeight?: number;
    railWidth?: number;
    body?: PageBodyVariant;
}

const pick = (slots: SurfaceSlot[], role: SlotRole) => slots.filter(s => s.role === role);

export const PageStructure = ({
    slots,
    renderSlot,
    maxWidth = 1200,
    gutter = 32,
    headerHeight = 56,
    railWidth = 340,
    body = "single",
}: PageStructureProps) => {
    const live = activeSlots(slots);

    const nav = pick(live, SlotRole.NAV);
    const stage = pick(live, SlotRole.STAGE);
    /*
     * A slot bound for the rail is one whose HUB footprint degrades to a band
     * but which is still a feature — i.e. the contested column's occupants. In
     * PAGE they are always-present, so they take the track.
     */
    const rail = body === "single"
        ? []
        : live.filter(s => s.role === SlotRole.FEATURE && s.footprint === SlotFootprint.COLUMN);
    const railIds = new Set(rail.map(s => s.id));
    const main = live.filter(s =>
        !railIds.has(s.id) && s.role !== SlotRole.NAV && s.role !== SlotRole.STAGE);

    return (
        <div
            className="surface-page"
            style={{
                "--page-max": maxWidth === "full" ? "none" : `${maxWidth}px`,
                "--page-gutter": `${gutter}px`,
                "--page-header-h": `${headerHeight}px`,
                "--page-rail": `${railWidth}px`,
            } as React.CSSProperties}
            data-page-body={body}
        >
            {nav.length > 0 &&
                <header className="surface-page__header">
                    {nav.map(s => <React.Fragment key={s.id}>{renderSlot(s)}</React.Fragment>)}
                </header>
            }

            <div className="surface-page__inner">
                {stage.map(s =>
                    <section className="surface-page__hero" key={s.id}>{renderSlot(s)}</section>)}

                <div className={`surface-page__body surface-page__body--${body}`}>
                    <div className="surface-page__main">
                        {main.map(s =>
                            <section
                                /*
                                 * PAGE_FORM is consulted rather than the raw
                                 * footprint: a COLUMN_WIDGET is a floated 392px
                                 * panel in HUB and a full-width band here, and
                                 * the slot config says nothing about which.
                                 */
                                className={PAGE_FORM[s.footprint] === SlotFootprint.HORIZONTAL_RAIL
                                    ? "surface-page__section surface-page__hrail"
                                    : "surface-page__section"}
                                key={s.id}
                            >
                                {renderSlot(s)}
                            </section>)}
                    </div>

                    {rail.length > 0 &&
                        <aside className="surface-page__rail">
                            {rail.map(s => <React.Fragment key={s.id}>{renderSlot(s)}</React.Fragment>)}
                        </aside>
                    }
                </div>
            </div>
        </div>
    );
};
